import BookIcon from '@mui/icons-material/Book';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import { Avatar, Box, Button, Card, Typography } from '@mui/material';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferListItem, OfferStatus, RequestType } from '../types';

interface OfferCardProps {
	offer: OfferListItem;
	onSendOffer?: (offerId: string) => void;
	onEditOffer?: (offerId: string) => void;
	onChat?: (request_id: string) => void;
	onViewDetails?: (offerId: string) => void;
	status?: OfferStatus;
}

export const OfferCard: React.FC<OfferCardProps> = ({
	offer,
	onSendOffer,
	onEditOffer,
	onChat,
	onViewDetails,
	status = 'active',
}) => {
	const { t } = useTranslation();

	const {
		id,
		crops,
		farmer,
		request_type,
		start_date,
		end_date,
		duration,
		is_sent,
		request_id,
		channelId,
		is_paid,
		request_active_days,
		payment_status,
		price,
		currency
	} = offer;
	
	// Convert price to number for proper comparison
	const numericPrice = typeof price === 'string' ? parseFloat(price) : Number(price || 0);
	
	const isSeasonalConsultation = request_type === RequestType.SEASONAL;
	const consultationType = isSeasonalConsultation
		? t('findSpecialistPage.seasonalConsultation')
		: t('ONE_TIME');

	const durationText =
		isSeasonalConsultation && duration
			? t('duration') +
				`: ${duration.months} ${duration.months === 1 ? t('month') : t('months')} ${t('and')} ${duration.days} ${duration.days === 1 ? t('day') : t('days')}`
			: t('duration') + `: 5 ${t('day')}`;

	const dateRange =
		isSeasonalConsultation && start_date && end_date
			? (() => {
				// For unpaid seasonal consultations, use original_date if available
				if (payment_status === 'unpaid' && offer.original_date) {
					console.log('Using original_date for unpaid seasonal consultation:', offer.original_date);
					return `${moment(offer.original_date.start_date).format('DD.MM.YYYY')} - ${moment(offer.original_date.end_date).format('DD.MM.YYYY')}`;
				}
				// Otherwise use the standard start_date and end_date
				return `${moment(start_date).format('DD.MM.YYYY')} - ${moment(end_date).format('DD.MM.YYYY')}`;
			})()
			: '';

	const wordedDateRange =
		isSeasonalConsultation && start_date && end_date
			? (() => {
					// For unpaid seasonal consultations, use original_date if available
					let startDateObj, endDateObj;
					
					if (payment_status === 'unpaid' && offer.original_date) {
						console.log('Using original_date for wordedDateRange calculation:', offer.original_date);
						startDateObj = moment(offer.original_date.start_date);
						endDateObj = moment(offer.original_date.end_date);
					} else {
						startDateObj = moment(start_date);
						endDateObj = moment(end_date);
					}
					
					const diffMonths = endDateObj.diff(startDateObj, 'months');
					const tempStartDate = moment(startDateObj).add(diffMonths, 'months');
					const remainingDays = endDateObj.diff(tempStartDate, 'day');

					if (diffMonths === 0) {
						return `${remainingDays} ${remainingDays === 1 ? t('day') : t('day')}`;
					}

					return `${diffMonths} ${diffMonths === 1 ? t('month') : t('months')} ${t('and')} ${remainingDays} ${remainingDays === 1 ? t('day') : t('days')}`;
				})()
			: '';

	// Check if the offer is paid (either is_paid flag or payment_status is 'paid' or 'stripe_payed')
	const isPaid = is_paid || payment_status === 'paid' || payment_status === 'stripe_payed' || payment_status === 'Accepted';
	
	// Calculate time remaining based on end_date
	const timeRemaining = useMemo(() => {
		if (end_date) {
			try {
				// For ISO format like "2025-07-25T13:15:08.000Z"
				const endDateObj = new Date(end_date);
				
				if (endDateObj instanceof Date && !isNaN(endDateObj.getTime())) {
					const currentDate = new Date();
					console.log('End date from API (Seasonal):', endDateObj);
					console.log('Current date:', currentDate);
					
					// Calculate time difference
					const diffTime = endDateObj.getTime() - currentDate.getTime();
					console.log('Diff time (ms):', diffTime);
					
					// Convert to hours and days
					const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
					const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
					console.log('Hours remaining:', diffHours);
					console.log('Days remaining:', diffDays);
					
					// If time is in the future
					if (diffTime > 0) {
						// If less than 1 day remains, show hours
						if (diffTime < 24 * 60 * 60 * 1000) {
							console.log('Using hours:', diffHours);
							return { value: diffHours, unit: 'hours' };
						} else {
							console.log('Using days:', diffDays);
							return { value: diffDays, unit: 'days' };
						}
					} else {
						// If time has expired, show 0 days
						console.log('Time expired, showing 0 days');
						return { value: 0, unit: 'days' };
					}
				}
			} catch (error) {
				console.error('Error calculating time remaining:', error);
			}
		}
		
		// Fallback to active_for_days or default values
		let days = Number(request_active_days || 3);
		
		// If we have duration, use that as fallback
		if (duration) {
			if (typeof duration === 'number') {
				days = duration;
			} else if (duration.months && duration.days) {
				days = duration.months * 30 + duration.days;
			}
		}
		
		console.log('Using fallback days:', days);
		return { value: days, unit: 'days' };
	}, [end_date, request_active_days, duration]);

	// Calculate status message based on payment status
	let statusMessage = '';
	if (isPaid) {
		// Paid status - "Chat closes in X days/hours"
		if (timeRemaining.unit === 'hours') {
			statusMessage = `Chat closes in ${timeRemaining.value} hours`;
		} else {
			statusMessage = `Chat closes in ${timeRemaining.value} days`;
		}
	} else {
		// Unpaid status - "Request active for X days"
		if (timeRemaining.value > 0) {
			statusMessage = `Request active for ${timeRemaining.value} days`;
		} else {
			// If days are negative or zero, show a generic message
			statusMessage = t('requestActive');
		}
	}

	const handleClick = () => {
		if (onViewDetails) {
			onViewDetails(id);
		}
	};

	return (
		<Card
			onClick={handleClick}
			sx={{
				borderRadius: '12px',
				cursor: 'pointer',
				boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.3)',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			{/* Top Section with Crop Info */}
			<Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
				<Box sx={{ display: 'flex', gap: 2 }}>
					<Avatar
						src={crops[0]?.image_url}
						alt={crops[0]?.name}
						sx={{ width: 60, height: 60, borderRadius: '12px' }}
					/>
					<Box>
						<Typography variant="h5" sx={{ fontWeight: 500, mb: 1 }}>
							{crops[0]?.name}
						</Typography>
						<Typography
							variant="subtitle1"
							sx={{
								color: '#3da08f',
								fontWeight: 500,
								mb: 1,
								borderBottom: '2px solid #3da08f',
								display: 'inline-block',
								paddingBottom: '2px',
							}}
						>
							{consultationType}
						</Typography>

						{request_type === RequestType.ONE_TIME ? (
							<Typography color="text.secondary" sx={{ mb: 0.5, fontWeight: '400 !important' }}>{durationText}</Typography>
						) : (
							<Typography color="text.secondary" sx={{ mb: 0.5, fontWeight: '400 !important' }}>
								{`${dateRange} (${wordedDateRange})`}
							</Typography>
						)}
					</Box>
				</Box>
			</Box>

			{/* Middle Section with Farmer Info */}
			<Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
				<Box sx={{ display: 'flex', gap: 2 }}>
					<Avatar
						src={farmer?.profile_image_url}
						alt={farmer?.full_name}
						sx={{ width: 60, height: 60, borderRadius: '12px' }}
					/>
					<Box>
						<Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
							{farmer?.full_name}
						</Typography>

						{/* Fieldbook Button */}
						<Button
							startIcon={<BookIcon />}
							onClick={(e) => e.stopPropagation()}
							disabled
							sx={{
								textTransform: 'none',
								padding: '0',
								color: '#3da08f',
								fontWeight: 500,
								'&:hover': {
									backgroundColor: 'transparent',
									textDecoration: 'underline',
								},
							}}
						>
							{t('fieldbook')}
						</Button>
					</Box>

					{numericPrice > 0 && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								ml: 'auto',
								width: '64px',
								height: '64px',
								backgroundColor: isPaid ? '#EBFDF9' : is_sent ? '#FFF7EA' : '#F8F9FA',
								borderRadius: '8px',
								border: isPaid
									? '1px solid #00AB89'
									: is_sent
										? '1px solid #FFAA2B'
										: '1px solid #E9ECEF',
							}}
						>
							<Typography
								variant="h4"
								sx={{
									fontWeight: 'bold',
									color: '#343a40',
								}}
							>
								{currency === 'EUR' ? '€' : currency}{numericPrice}
							</Typography>
						</Box>
					)}
				</Box>
			</Box>

			{/* Bottom Section with Status and Actions */}
			<Box
				sx={{
					p: 2,
					mt: 'auto',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					backgroundColor: '#F8F9FA',
				}}
			>
				{status === 'active' && (
					<Typography variant="body2" color="text.secondary" sx={{ fontWeight: '400 !important' }}>
						{statusMessage}
					</Typography>
				)}

				<Typography variant="h6" sx={{ ml: status === 'active' ? 'auto' : '0', mr: 2, fontWeight: '400 !important' }}>
					{end_date 
						? moment(end_date).format('DD.MM.YYYY')
						: start_date
							? moment(start_date).format('DD.MM.YYYY')
							: ''}
				</Typography>
			</Box>

			{/* Action Buttons - Only show for active offers */}
			{status === 'active' && (
				<Box
					sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}
				>
					<Button
						variant={isPaid ? 'contained' : 'text'}
						disabled={!channelId}
						onClick={(e) => {
							e.stopPropagation();
							if (onChat && request_id) onChat(request_id);
						}}
						sx={{
							padding: '10px 20px',
							fontWeight: '700',
							fontSize: '14px',
							border: 'unset',
							...(isPaid
								? {
										backgroundColor: '#00AB89',
										'&:hover': {
											backgroundColor: '#008C6F',
										},
									}
								: {
										color: '#00AB89',
										textDecoration: 'underline',
										'&:hover': {
											backgroundColor: 'transparent',
											textDecoration: 'underline',
										},
									}),
						}}
					>
						<QuestionAnswerOutlinedIcon
							className={`mr-[10px] ${!isPaid ? 'text-[#00AB89]' : 'text-white'}`}
						/>
						<span className={!isPaid ? 'text-[#00AB89]' : 'text-white'}>{t('chat')}</span>
					</Button>

					{isPaid ? (
						<Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
							<CheckCircleIcon sx={{ color: '#3da08f', mr: 1 }} />
							<Typography sx={{ color: '#3da08f', fontWeight: 'bold' }}>
								{payment_status === 'Accepted' ? t('accepted', 'Accepted') : t('paid')}
							</Typography>
						</Box>
					) : (
						<Button
							variant="contained"
							onClick={(e) => {
								e.stopPropagation();
								if (onSendOffer) onSendOffer(id);
								if (onEditOffer) onEditOffer(id);
							}}
							sx={{
								padding: '10px 20px',
								fontWeight: '700',
								fontSize: '14px',
								border: 'unset',
								backgroundColor: is_sent ? '#FFAA2B' : '#00AB89',
								color: 'white',
							}}
							disabled={payment_status === 'paid' || payment_status === 'stripe_payed' || payment_status === 'Accepted'}
						>
							{is_sent ? (
								payment_status === 'paid' || payment_status === 'stripe_payed' 
									? t('paid') 
									: payment_status === 'Accepted'
										? t('accepted', 'Accepted')
										: t('editOffer')
							) : t('sendOffer')}
						</Button>
					)}
				</Box>
			)}
		</Card>
	);
};
